import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, X, Plus, Minus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError, mediaUrl } from "../lib/api";
import { EText } from "../components/cms/Editable";
import { useAuth } from "../context/AuthContext";

export default function Shop() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [cat, setCat] = useState("all");
  const [params] = useSearchParams();

  const cats = ["all", ...new Set(products.map((p) => p.category))];
  const shown = products.filter((p) => cat === "all" || p.category === cat);

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      const pid = params.get("product");
      if (pid) {
        const p = r.data.find((x) => x.id === pid);
        if (p) setDetail(p);
      }
    });
  }, []);

  const add = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.id === p.id);
      if (ex) return c.map((i) => i.id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { id: p.id, name: p.name, price: p.price, qty: 1 }];
    });
    toast.success(`${p.name} added to cart`);
  };
  const setQty = (id, d) => setCart((c) => c.map((i) => i.id === id ? { ...i, qty: Math.max(1, i.qty + d) } : i));
  const remove = (id) => setCart((c) => c.filter((i) => i.id !== id));
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const checkout = async () => {
    try {
      await api.post("/orders", { items: cart, email: user?.email });
      toast.success("Order placed! Payment step will follow once checkout is connected.");
      setCart([]); setOpen(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Layout>
      <section className="section-pad">
        <div className="flex items-center justify-between mb-10">
          <div>
            <EText k="shop.pill" className="pill bg-brand-cream2 text-brand-tan">Healing Essentials</EText>
            <EText k="shop.title" as="h1" className="font-display text-4xl sm:text-5xl font-semibold text-brand-ink mt-3 block">The Zenergize Shop</EText>
          </div>
          <button onClick={() => setOpen(true)} className="relative btn-outline !py-2" data-testid="cart-button">
            <ShoppingCart size={18} /> Cart
            {cart.length > 0 && <span className="absolute -top-2 -right-2 bg-brand-gold text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cart.reduce((s, i) => s + i.qty, 0)}</span>}
          </button>
        </div>

        <p className="text-xs text-brand-muted mb-6">The complete Isoharts catalog — {products.length} products: memberships, books, videos & audio, oil blends, vision training, retreats, gifts, and courses.</p>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="shop-category-filters">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`shop-cat-${c === "all" ? "all" : c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${cat === c ? "bg-brand-sage text-white" : "bg-white border border-brand-line text-brand-inksoft hover:border-brand-sage"}`}>
              {c === "all" ? `All (${products.length})` : c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {shown.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="card-surface overflow-hidden flex flex-col group" data-testid={`product-${p.id}`}>
              <button onClick={() => setDetail(p)} className="bg-brand-cream2 aspect-[4/3] w-full overflow-hidden" data-testid={`product-img-${p.id}`}>
                <img src={mediaUrl(p.image)} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </button>
              <div className="p-4 flex-1 flex flex-col border-t border-brand-line">
                <span className="text-[10px] uppercase tracking-wide text-brand-muted capitalize">{p.kind}</span>
                <h4 className="font-medium text-brand-ink text-sm mt-0.5 leading-snug">{p.name}</h4>
                <p className="text-xs text-brand-muted mt-1 flex-1 line-clamp-2">{p.description}</p>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="font-display text-lg font-semibold text-brand-ink leading-tight">{p.price_label}</span>
                  {p.kind === "membership" ? (
                    <Link to={p.tier === "silver" ? "/signup" : `/packages?tier=${p.tier}&checkout=true`} className="btn-gold !py-1.5 !px-3 text-xs whitespace-nowrap" data-testid={`view-${p.id}`}>
                      View
                    </Link>
                  ) : p.purchasable ? (
                    <button onClick={() => add(p)} className="btn-primary !py-1.5 !px-3 text-xs whitespace-nowrap" data-testid={`add-${p.id}`}>Add to Cart</button>
                  ) : (
                    <button onClick={() => setDetail(p)} className="btn-outline !py-1.5 !px-3 text-xs whitespace-nowrap" data-testid={`details-${p.id}`}>Details</button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-5" onClick={() => setDetail(null)}>
          <div className="card-surface max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="product-detail-modal">
            <div className="bg-white flex items-center justify-center p-8 h-64 relative">
              <img src={mediaUrl(detail.image)} alt={detail.name} className="max-w-full max-h-full object-contain" />
              <button onClick={() => setDetail(null)} className="absolute top-3 right-3 p-2 rounded-full bg-brand-cream hover:bg-brand-cream2" data-testid="product-detail-close"><X size={16} /></button>
            </div>
            <div className="p-6 border-t border-brand-line">
              <span className="pill bg-brand-cream2 text-brand-tan capitalize">{detail.kind}</span>
              <h3 className="font-display text-2xl font-semibold text-brand-ink mt-2">{detail.name}</h3>
              <p className="text-brand-gold font-semibold mt-1">{detail.price_label}</p>
              <p className="text-sm text-brand-inksoft mt-3 leading-relaxed">{detail.description}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {detail.kind === "membership" ? (
                  <Link to={detail.tier === "silver" ? "/signup" : `/packages?tier=${detail.tier}&checkout=true`} className="btn-gold !py-2 text-sm" data-testid="detail-membership-cta">View Membership</Link>
                ) : detail.purchasable ? (
                  <button onClick={() => { add(detail); setDetail(null); setOpen(true); }} className="btn-primary !py-2 text-sm" data-testid="detail-add-cart">Add to Cart</button>
                ) : null}
                <a href={detail.url} target="_blank" rel="noreferrer" className="btn-outline !py-2 text-sm" data-testid="detail-source-link">Full details on isoharts.com <ExternalLink size={14} /></a>
              </div>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={() => setOpen(false)}>
          <div className="bg-brand-cream w-full max-w-md h-full p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cart-drawer">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl font-semibold text-brand-ink">Your Cart</h3>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>
            {cart.length === 0 ? (
              <p className="text-brand-muted mt-8 text-center">Your cart is empty.</p>
            ) : (
              <>
                <div className="mt-6 space-y-4">
                  {cart.map((i) => (
                    <div key={i.id} className="card-surface p-4 flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-brand-ink">{i.name}</p>
                        <p className="text-xs text-brand-muted">${i.price} each</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQty(i.id, -1)} className="p-1 border rounded"><Minus size={12} /></button>
                        <span className="text-sm w-5 text-center">{i.qty}</span>
                        <button onClick={() => setQty(i.id, 1)} className="p-1 border rounded"><Plus size={12} /></button>
                      </div>
                      <button onClick={() => remove(i.id)} className="text-red-400"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <span className="text-brand-muted">Total</span>
                  <span className="font-display text-3xl font-semibold text-brand-ink">${total.toFixed(2)}</span>
                </div>
                <button onClick={checkout} className="btn-primary w-full mt-4" data-testid="checkout-button">Checkout</button>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
